import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const initialFormData = {
  id: '',
  title: '',
  owner_id: '',
  description: '',
  transaction_type: 'sale',
  property_type: 'house',
  status: 'available',
  deal_date: '',
  featured: false,
  construction_year: null,
  condition: '',
  created_at: '',
  updated_at: '',
  address: {
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: 'Toledo',
    state: 'PR',
    zip_code: '',
    hide_exact_address: false,
    latitude: null,
    longitude: null,
  },
  pricing: {
    sale_price: null,
    rent_price: null,
    condo_fee: null,
    iptu: null,
  },
  rooms: {
    bedrooms: 0,
    bathrooms: 0,
    suites: 0,
    living_rooms: 0,
    dining_rooms: 0,
    kitchens: 1,
    offices: 0,
    laundry: 0,
    balconies: 0,
  },
  garage: {
    parking_spots: 0,
    covered_spots: 0,
    uncovered_spots: 0,
  },
  sizes: {
    built_area_m2: null,
    total_area_m2: null,
    lot_size_m2: null,
  },
  floor_number: null,
  elevators_count: null,
  amenities: [],
  tags: [],
  images: [],
  featured_image: '',
  video_url: '',
  virtual_tour_url: '',
  furnished: false,
  is_new: false,
  pet_friendly: false,
  accessibility: false,
};

export default function PropertyForm() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [amenities, setAmenities] = useState([]);
  const [tags, setTags] = useState([]);
  const [owners, setOwners] = useState([]);
  const [formData, setFormData] = useState(initialFormData);

  const fetchProperty = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await api.get(`/properties/${id}`);
      setFormData({
        ...initialFormData,
        ...response.data,
        address: {
          ...initialFormData.address,
          ...(response.data.address || {}),
        },
        pricing: {
          ...initialFormData.pricing,
          ...(response.data.pricing || {}),
        },
        rooms: {
          ...initialFormData.rooms,
          ...(response.data.rooms || {}),
        },
        garage: {
          ...initialFormData.garage,
          ...(response.data.garage || {}),
        },
        sizes: {
          ...initialFormData.sizes,
          ...(response.data.sizes || {}),
        },
        amenities: response.data.amenities || [],
        tags: response.data.tags || [],
        images: response.data.images || [],
        featured_image: response.data.featured_image || '',
        owner_id: response.data.owner_id || '',
      });
    } catch (error) {
      toast.error('Erro ao carregar imóvel.');
      navigate('/admin/properties');
    } finally {
      setLoading(false);
    }
  }, [api, id, navigate]);

  const fetchOptions = useCallback(async () => {
    try {
      const [amenitiesRes, tagsRes] = await Promise.all([
        api.get('/amenities'),
        api.get('/tags'),
      ]);
      setAmenities(amenitiesRes.data || []);
      setTags(tagsRes.data || []);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  }, [api]);

  const fetchOwners = useCallback(async () => {
    try {
      const response = await api.get('/owners');
      setOwners(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar proprietários:', error);
      setOwners([]);
    }
  }, [api]);

  useEffect(() => {
    fetchOwners();
    fetchOptions();
    fetchProperty();
  }, [fetchOwners, fetchOptions, fetchProperty]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  };

  const handleAmenityToggle = (amenityName) => {
    setFormData((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenityName)
        ? prev.amenities.filter((a) => a !== amenityName)
        : [...prev.amenities, amenityName],
    }));
  };

  const handleTagToggle = (tagName) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagName)
        ? prev.tags.filter((t) => t !== tagName)
        : [...prev.tags, tagName],
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    if (!isEditing || !id) {
      toast.error('Salve o imóvel antes de enviar imagens.');
      return;
    }

    try {
      const uploadedImages = [];

      for (const file of files) {
        const data = new FormData();
        data.append('file', file);

        const response = await api.post(`/upload/property/${id}`, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        uploadedImages.push(response.data.image_url);
      }

      setFormData((prev) => ({
        ...prev,
        images: [...(prev.images || []), ...uploadedImages],
      }));

      toast.success('Imagem(ns) enviada(s) com sucesso!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao enviar imagem(ns).');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      toast.error('Título é obrigatório.');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await api.put(`/properties/${id}`, formData);
        toast.success('Imóvel atualizado com sucesso!');
      } else {
        await api.post('/properties', formData);
        toast.success('Imóvel cadastrado com sucesso!');
      }

      navigate('/admin/properties');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao salvar imóvel.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="property-form">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/properties')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800">
            {isEditing ? 'Editar Imóvel' : 'Novo Imóvel'}
          </h1>
          {formData.id && (
            <p className="text-sm text-slate-500 mt-1">
              Código do imóvel: <span className="font-medium text-slate-700">{formData.id}</span>
            </p>
          )}

          {formData.created_at && (
            <p className="text-sm text-slate-500 mt-1">
              Cadastrado no CRM em:{' '}
              <span className="font-medium text-slate-700">
                {new Date(formData.created_at).toLocaleString('pt-BR', {
                  timeZone: 'America/Sao_Paulo'
                })}
              </span>
            </p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="basic" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-5">
            <TabsTrigger value="basic">Básico</TabsTrigger>
            <TabsTrigger value="address">Endereço</TabsTrigger>
            <TabsTrigger value="details">Detalhes</TabsTrigger>
            <TabsTrigger value="amenities">Comodidades</TabsTrigger>
            <TabsTrigger value="media">Mídia</TabsTrigger>
          </TabsList>

          <TabsContent value="basic">
            <Card>
              <CardHeader>
                <CardTitle>Informações Básicas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="title">Título *</Label>
                    <Input
                      id="title"
                      data-testid="property-title"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      placeholder="Ex: Casa Alto Padrão no Jardim La Salle"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Tipo de Transação</Label>
                    <Select
                      value={formData.transaction_type}
                      onValueChange={(v) => handleChange('transaction_type', v)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sale">Venda</SelectItem>
                        <SelectItem value="rent">Aluguel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Tipo de Imóvel</Label>
                    <Select
                      value={formData.property_type}
                      onValueChange={(v) => handleChange('property_type', v)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="house">Casa</SelectItem>
                        <SelectItem value="apartment">Apartamento</SelectItem>
                        <SelectItem value="land">Terreno</SelectItem>
                        <SelectItem value="commercial">Comercial</SelectItem>
                        <SelectItem value="rural">Rural</SelectItem>
                        <SelectItem value="chacara">Chácara</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(v) => handleChange('status', v)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Disponível</SelectItem>
                        <SelectItem value="sold">Vendido</SelectItem>
                        <SelectItem value="rented">Alugado</SelectItem>
                        <SelectItem value="inactive">Inativo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.status === 'sold' && (
                    <div>
                      <Label>Data do Negócio</Label>
                      <Input
                        type="date"
                        value={formData.deal_date || ''}
                        onChange={(e) => handleChange('deal_date', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  )}

                  <div>
                    <Label>Proprietário</Label>
                    <Select
                      value={formData.owner_id || 'none'}
                      onValueChange={(value) => handleChange('owner_id', value === 'none' ? '' : value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Selecione um proprietário" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem proprietário vinculado</SelectItem>
                        {owners.map((owner) => (
                          <SelectItem key={owner.id} value={owner.id}>
                            {owner.name}{owner.cpf ? ` - ${owner.cpf}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="featured"
                        checked={formData.featured}
                        onCheckedChange={(v) => handleChange('featured', v)}
                      />
                      <Label htmlFor="featured">Destaque</Label>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Ano de Construção</Label>
                      <Input
                        type="number"
                        value={formData.construction_year || ''}
                        onChange={(e) =>
                          handleChange(
                            'construction_year',
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                        placeholder="Ex: 2018"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Conservação</Label>
                      <Select
                        value={formData.condition || 'none'}
                        onValueChange={(value) =>
                          handleChange('condition', value === 'none' ? '' : value)
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Selecione</SelectItem>
                          <SelectItem value="Boa">Boa</SelectItem>
                          <SelectItem value="Ruim">Ruim</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Descreva o imóvel..."
                    rows={4}
                    className="mt-1"
                  />
                </div>

                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-4">Valores</h4>
                  <div className="grid md:grid-cols-4 gap-4">
                    {formData.transaction_type === 'sale' ? (
                      <div>
                        <Label>Preço de Venda</Label>
                        <Input
                          type="number"
                          value={formData.pricing.sale_price || ''}
                          onChange={(e) =>
                            handleNestedChange(
                              'pricing',
                              'sale_price',
                              e.target.value ? Number(e.target.value) : null
                            )
                          }
                          placeholder="0"
                          className="mt-1"
                        />
                      </div>
                    ) : (
                      <div>
                        <Label>Aluguel Mensal</Label>
                        <Input
                          type="number"
                          value={formData.pricing.rent_price || ''}
                          onChange={(e) =>
                            handleNestedChange(
                              'pricing',
                              'rent_price',
                              e.target.value ? Number(e.target.value) : null
                            )
                          }
                          placeholder="0"
                          className="mt-1"
                        />
                      </div>
                    )}

                    <div>
                      <Label>Condomínio</Label>
                      <Input
                        type="number"
                        value={formData.pricing.condo_fee || ''}
                        onChange={(e) =>
                          handleNestedChange(
                            'pricing',
                            'condo_fee',
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                        placeholder="0"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>IPTU Anual</Label>
                      <Input
                        type="number"
                        value={formData.pricing.iptu || ''}
                        onChange={(e) =>
                          handleNestedChange(
                            'pricing',
                            'iptu',
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                        placeholder="0"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="address">
            <Card>
              <CardHeader>
                <CardTitle>Endereço</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <Label>Rua</Label>
                    <Input
                      value={formData.address.street}
                      onChange={(e) => handleNestedChange('address', 'street', e.target.value)}
                      placeholder="Nome da rua"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Número</Label>
                    <Input
                      value={formData.address.number}
                      onChange={(e) => handleNestedChange('address', 'number', e.target.value)}
                      placeholder="123"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Complemento</Label>
                    <Input
                      value={formData.address.complement}
                      onChange={(e) => handleNestedChange('address', 'complement', e.target.value)}
                      placeholder="Apto, Sala, etc"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Bairro</Label>
                    <Input
                      value={formData.address.neighborhood}
                      onChange={(e) => handleNestedChange('address', 'neighborhood', e.target.value)}
                      placeholder="Bairro"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>CEP</Label>
                    <Input
                      value={formData.address.zip_code}
                      onChange={(e) => handleNestedChange('address', 'zip_code', e.target.value)}
                      placeholder="00000-000"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Cidade</Label>
                    <Input
                      value={formData.address.city}
                      onChange={(e) => handleNestedChange('address', 'city', e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Estado</Label>
                    <Input
                      value={formData.address.state}
                      onChange={(e) => handleNestedChange('address', 'state', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-4">
                  <Switch
                    id="hide_address"
                    checked={formData.address.hide_exact_address}
                    onCheckedChange={(v) => handleNestedChange('address', 'hide_exact_address', v)}
                  />
                  <Label htmlFor="hide_address">Ocultar endereço exato (mostrar apenas bairro)</Label>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="details">
            <Card>
              <CardHeader>
                <CardTitle>Detalhes do Imóvel</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-4">Cômodos</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <Label>Quartos</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.rooms.bedrooms}
                        onChange={(e) =>
                          handleNestedChange('rooms', 'bedrooms', parseInt(e.target.value) || 0)
                        }
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Banheiros</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.rooms.bathrooms}
                        onChange={(e) =>
                          handleNestedChange('rooms', 'bathrooms', parseInt(e.target.value) || 0)
                        }
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Suítes</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.rooms.suites}
                        onChange={(e) =>
                          handleNestedChange('rooms', 'suites', parseInt(e.target.value) || 0)
                        }
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Salas</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.rooms.living_rooms}
                        onChange={(e) =>
                          handleNestedChange('rooms', 'living_rooms', parseInt(e.target.value) || 0)
                        }
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Varandas</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.rooms.balconies}
                        onChange={(e) =>
                          handleNestedChange('rooms', 'balconies', parseInt(e.target.value) || 0)
                        }
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-4">Garagem</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Total de Vagas</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.garage.parking_spots}
                        onChange={(e) =>
                          handleNestedChange('garage', 'parking_spots', parseInt(e.target.value) || 0)
                        }
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Cobertas</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.garage.covered_spots}
                        onChange={(e) =>
                          handleNestedChange('garage', 'covered_spots', parseInt(e.target.value) || 0)
                        }
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Descobertas</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.garage.uncovered_spots}
                        onChange={(e) =>
                          handleNestedChange('garage', 'uncovered_spots', parseInt(e.target.value) || 0)
                        }
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-4">Áreas (m²)</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Área Construída</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.sizes.built_area_m2 || ''}
                        onChange={(e) =>
                          handleNestedChange(
                            'sizes',
                            'built_area_m2',
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Área Total</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.sizes.total_area_m2 || ''}
                        onChange={(e) =>
                          handleNestedChange(
                            'sizes',
                            'total_area_m2',
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Área do Terreno</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.sizes.lot_size_m2 || ''}
                        onChange={(e) =>
                          handleNestedChange(
                            'sizes',
                            'lot_size_m2',
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {formData.property_type === 'apartment' && (
                  <div className="pt-4 border-t">
                    <h4 className="font-semibold mb-4">Informações do Apartamento</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Andar</Label>
                        <Input
                          type="number"
                          min="0"
                          value={formData.floor_number || ''}
                          onChange={(e) =>
                            handleChange('floor_number', e.target.value ? parseInt(e.target.value) : null)
                          }
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Elevadores</Label>
                        <Input
                          type="number"
                          min="0"
                          value={formData.elevators_count || ''}
                          onChange={(e) =>
                            handleChange(
                              'elevators_count',
                              e.target.value ? parseInt(e.target.value) : null
                            )
                          }
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-4">Características</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="furnished"
                        checked={formData.furnished}
                        onCheckedChange={(v) => handleChange('furnished', v)}
                      />
                      <Label htmlFor="furnished">Mobiliado</Label>
                    </div>

                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="is_new"
                        checked={formData.is_new}
                        onCheckedChange={(v) => handleChange('is_new', v)}
                      />
                      <Label htmlFor="is_new">Novo</Label>
                    </div>

                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="pet_friendly"
                        checked={formData.pet_friendly}
                        onCheckedChange={(v) => handleChange('pet_friendly', v)}
                      />
                      <Label htmlFor="pet_friendly">Aceita Pets</Label>
                    </div>

                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="accessibility"
                        checked={formData.accessibility}
                        onCheckedChange={(v) => handleChange('accessibility', v)}
                      />
                      <Label htmlFor="accessibility">Acessibilidade</Label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="amenities">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Comodidades</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {amenities.map((amenity) => (
                      <div key={amenity.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`amenity-${amenity.id}`}
                          checked={formData.amenities.includes(amenity.name)}
                          onCheckedChange={() => handleAmenityToggle(amenity.name)}
                        />
                        <Label htmlFor={`amenity-${amenity.id}`} className="text-sm">
                          {amenity.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Tags / Badges</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {tags.map((tag) => (
                      <div key={tag.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`tag-${tag.id}`}
                          checked={formData.tags.includes(tag.name)}
                          onCheckedChange={() => handleTagToggle(tag.name)}
                        />
                        <Label htmlFor={`tag-${tag.id}`} className="text-sm">
                          {tag.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="media">
            <Card>
              <CardHeader>
                <CardTitle>Mídia</CardTitle>
              </CardHeader>

              <CardContent className="space-y-6">
                <div>
                  <Label>URL do Vídeo (YouTube, Vimeo)</Label>
                  <Input
                    value={formData.video_url || ''}
                    onChange={(e) => handleChange('video_url', e.target.value)}
                    placeholder="https://youtube.com/..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>URL do Tour Virtual</Label>
                  <Input
                    value={formData.virtual_tour_url || ''}
                    onChange={(e) => handleChange('virtual_tour_url', e.target.value)}
                    placeholder="https://..."
                    className="mt-1"
                  />
                </div>

                <div>
                  {!isEditing && (
                    <p className="text-sm text-slate-500 mb-2">
                      Salve o imóvel primeiro para habilitar o upload de imagens.
                    </p>
                  )}

                  <Label>Imagens do Imóvel</Label>

                  <div className="mt-2">
                    <label htmlFor="property-images-upload">
                      <span className="inline-flex items-center justify-center rounded-md bg-brand-blue hover:bg-brand-blue-dark text-white px-4 py-2 cursor-pointer transition-colors">
                        <Upload className="w-4 h-4 mr-2" />
                        Enviar Imagens
                      </span>
                    </label>

                    <input
                      id="property-images-upload"
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                  </div>
                </div>

                {formData.images && formData.images.length > 0 && (
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    {formData.images.map((img, index) => (
                      <div key={index} className="relative">
                        <button
                          type="button"
                          onClick={() =>
                            handleChange(
                              'featured_image',
                              typeof img === 'string'
                                ? img
                                : URL.createObjectURL(img)
                            )
                          }
                          className={`absolute top-1 left-1 z-10 text-xs px-2 py-1 rounded shadow ${formData.featured_image === img
                              ? 'bg-yellow-400 text-black'
                              : 'bg-black/70 text-white'
                            }`}
                        >
                          {formData.featured_image === (
                            typeof img === 'string'
                              ? img
                              : URL.createObjectURL(img)
                          )
                            ? 'Capa'
                            : 'Definir capa'}
                        </button>
                        <img
                          src={
                            typeof img === 'string'
                              ? `${process.env.REACT_APP_BACKEND_URL}${img}`
                              : URL.createObjectURL(img)
                          }
                          alt={`Imagem ${index}`}
                          className="w-full h-32 object-cover rounded"
                        />

                        <button
                          type="button"
                          onClick={() => {
                            const newImages = formData.images.filter((_, i) => i !== index);
                            setFormData((prev) => ({
                              ...prev,
                              images: newImages,
                            }));
                          }}
                          className="absolute top-1 right-1 bg-red-500 text-white text-xs px-2 py-1 rounded"
                        >
                          X
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-4 pt-6">
          <Button type="button" variant="outline" onClick={() => navigate('/admin/properties')}>
            Cancelar
          </Button>

          <Button
            type="submit"
            disabled={saving}
            className="bg-brand-blue hover:bg-brand-blue-dark"
            data-testid="save-property-btn"
          >
            {saving ? (
              'Salvando...'
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? 'Atualizar Imóvel' : 'Cadastrar Imóvel'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}